import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from 'react';
import { SellerAccountBasicInfo } from 'src/components/SellerAccountBasicInfo';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content } from 'src/contexts/ContentContext';
import { SearchResultPickerDialog } from 'src/dialogs/SearchResultPickerDialog';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import { sortSellerAccount } from 'src/utils/userUtils';
import {
  LoginClient,
  SellerAccount,
  SellerAccountClient,
} from 'src/WebApiController';

type SwitchAccountContextType = {
  onSwitchAccountClicked: () => void;
};

const SwitchAccountContext = createContext<SwitchAccountContextType | null>(
  null
);

export const useSwitchAccount = () => {
  const context = useContext(SwitchAccountContext);
  if (!context) {
    throw new Error(
      'useSwitchAccount must be used within a SwitchAccountProvider'
    );
  }
  return context;
};

export const SwitchAccountProvider = ({ children }: PropsWithChildren) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();

  // Account switch dialog state
  const [sellerAccounts, setSellerAccounts] = useState<SellerAccount[]>([]);
  const [filteredSellerAccounts, setFilteredSellerAccounts] = useState<
    SellerAccount[]
  >([]);
  const [isSellerAccountLoading, setIsSellerAccountLoading] = useState(false);
  const { closeDialog, launchDialog, dialogProps } = useBasicDialog();

  const onFilterSellerAccount = useCallback(
    (text: string) => {
      text = text.toLocaleUpperCase();
      const filtered = sellerAccounts.filter(
        (sa) =>
          sa.accountName.toLocaleUpperCase().includes(text) ||
          sa.impersonatedUsername?.toLocaleUpperCase()?.includes(text) ||
          sa.id.toLocaleUpperCase().includes(text)
      );
      setFilteredSellerAccounts(filtered);
    },
    [sellerAccounts]
  );

  const onSwitchAccountClicked = useCallback(async () => {
    const accounts = await tryInvokeApi(
      async () => {
        setIsSellerAccountLoading(true);
        return await new SellerAccountClient(
          activeAccountWebClientConfig
        ).getCurrentUserSellerAccounts();
      },
      (error) => {
        showErrorDialog(
          'SellerAccountClient.getCurrentUserSellerAccounts',
          error
        );
      },
      () => setIsSellerAccountLoading(false)
    );
    if (accounts) {
      const sorted = sortSellerAccount(accounts);
      setSellerAccounts(sorted);
      setFilteredSellerAccounts(sorted);
      launchDialog();
    }
  }, [activeAccountWebClientConfig, launchDialog, showErrorDialog]);

  const onSellerAccountPicked = useCallback(
    async (account: SellerAccount) => {
      if (account.id === activeAccountWebClientConfig.activeAccountId) {
        return;
      }

      await tryInvokeApi(
        async () => {
          setIsSellerAccountLoading(true);
          await new LoginClient(activeAccountWebClientConfig).setActiveAccount(
            account.id,
            account.impersonatedUserId
          );
          // We need to reload so all the data is reload with the new login context
          window.location.reload();
          closeDialog();
        },
        (error) => {
          showErrorDialog('LoginClient.setActiveAccount', error, {
            trackErrorData: account,
          });
        },
        () => setIsSellerAccountLoading(false)
      );
    },
    [activeAccountWebClientConfig, closeDialog, showErrorDialog]
  );

  return (
    <SwitchAccountContext.Provider value={{ onSwitchAccountClicked }}>
      {children}

      {/* Account switch dialog */}
      <SearchResultPickerDialog
        {...dialogProps}
        header={<Content id={ContentId.SelectAccountToSwitch} />}
        resultItems={filteredSellerAccounts ?? []}
        isLoading={isSellerAccountLoading}
        renderItem={(account: SellerAccount) => (
          <SellerAccountBasicInfo
            account={account}
            isCurrent={
              account.id === activeAccountWebClientConfig.activeAccountId
            }
          />
        )}
        onResultItemSearched={onFilterSellerAccount}
        onResultItemPicked={onSellerAccountPicked}
        onCancel={closeDialog}
      />
    </SwitchAccountContext.Provider>
  );
};
